"use strict";
// =========================================================================
// Print stylesheet for rendered form submissions. Engine-agnostic: written
// against the subset of paged-media CSS that both WeasyPrint and headless
// Chromium honour (@page, page-break-inside; colours string-interpolated, no
// var() dependency).
//
// White-label ready: branding (colours, logo, org name) is supplied per call
// via PdfBrandTheme rather than baked in. Default theme = Stellenbosch
// University (Confident Maroon #61223B, Brilliant Gold #B79962).
// =========================================================================
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildFormPdfStyles = exports.DEFAULT_PDF_THEME = void 0;
exports.DEFAULT_PDF_THEME = {
    primaryColor: '#61223B',
    secondaryColor: '#B79962',
    orgName: 'Stellenbosch University',
};
const buildFormPdfStyles = (theme) => {
    const primary = theme.primaryColor;
    const secondary = theme.secondaryColor;
    return `
  @page {
    size: A4;
    margin: 18mm 16mm 20mm 16mm;
    @bottom-center {
      content: "Page " counter(page) " of " counter(pages);
      font-size: 8pt;
      color: #888;
    }
  }

  * { box-sizing: border-box; }

  body {
    font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-size: 10.5pt;
    line-height: 1.45;
    color: #1c1c1c;
    margin: 0;
  }

  /* ---- Document header ---- */
  .doc-header { margin-bottom: 14px; }
  .doc-header .brand-bar {
    height: 6px;
    background: linear-gradient(90deg, ${primary} 0%, ${primary} 70%, ${secondary} 70%, ${secondary} 100%);
    border-radius: 2px;
    margin-bottom: 10px;
  }
  .doc-header .brand-logo { max-height: 48px; max-width: 220px; margin-bottom: 8px; }
  .doc-header h1 {
    color: ${primary};
    font-size: 18pt;
    margin: 0 0 2px 0;
  }
  .doc-header .subtitle {
    color: ${secondary};
    font-size: 9.5pt;
    letter-spacing: 0.3px;
    margin-bottom: 10px;
  }
  table.meta { width: 100%; border-collapse: collapse; font-size: 9pt; }
  table.meta td { padding: 2px 8px 2px 0; color: #444; vertical-align: top; }
  table.meta td.k { color: #888; white-space: nowrap; width: 1%; }

  /* ---- Score summary ---- */
  .scores { margin: 16px 0 20px 0; page-break-inside: avoid; }
  .scores h2 {
    color: ${primary}; font-size: 12pt; margin: 0 0 6px 0;
    border-bottom: 1px solid #e3d9c9; padding-bottom: 4px;
  }
  table.scores-tbl { width: 100%; border-collapse: collapse; }
  table.scores-tbl td, table.scores-tbl th {
    padding: 5px 8px; font-size: 9.5pt; border-bottom: 1px solid #eee; text-align: left;
  }
  table.scores-tbl td.v { text-align: right; font-variant-numeric: tabular-nums; width: 1%; white-space: nowrap; }
  table.scores-tbl tr.total td {
    border-top: 2px solid ${primary}; border-bottom: none;
    font-weight: 700; color: ${primary}; padding-top: 7px;
  }

  /* ---- Sections ---- */
  section.form-section { margin-bottom: 18px; }
  section.form-section > h2 {
    color: #fff; background: ${primary};
    font-size: 11.5pt; margin: 0 0 10px 0; padding: 6px 10px; border-radius: 3px;
  }
  .section-subheader { color: #555; font-size: 9.5pt; margin: -4px 0 10px 0; }
  .subsection-title {
    color: ${primary}; font-size: 10pt; font-weight: 700;
    margin: 12px 0 4px 0; border-bottom: 1px dotted #d8ccb6; padding-bottom: 2px;
  }
  .subsection-desc { color: #666; font-size: 8.5pt; margin: 0 0 8px 0; }

  /* ---- Question / answer block ---- */
  .qa { margin: 0 0 10px 0; page-break-inside: avoid; }
  .qa .label { font-weight: 600; color: #2a2a2a; font-size: 10pt; }
  .qa .label .req { color: #b00020; margin-left: 2px; }
  .qa .help { color: #777; font-size: 8.5pt; margin: 1px 0 0 0; }
  .qa .answer {
    margin-top: 3px; padding: 5px 9px; background: #faf8f4;
    border-left: 3px solid ${secondary}; border-radius: 2px;
    white-space: pre-wrap; word-break: break-word; min-height: 14px;
  }
  .qa .answer.empty { color: #aaa; font-style: italic; }
  .tag {
    display: inline-block; font-size: 7.5pt; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.4px; padding: 1px 5px; border-radius: 3px; margin-left: 6px;
    background: #fce8ec; color: #8a1c34; vertical-align: middle;
  }

  ul.multi, ul.files { margin: 0; padding-left: 16px; }
  ul.multi li, ul.files li { margin: 1px 0; }
  ul.files .fmeta { color: #999; font-size: 8.5pt; }
  ul.files a { color: ${primary}; text-decoration: underline; word-break: break-word; }

  /* ---- Repeater / table ---- */
  table.repeater { width: 100%; border-collapse: collapse; margin-top: 3px; }
  table.repeater th {
    background: ${primary}; color: #fff; font-size: 8.5pt; text-align: left;
    padding: 4px 7px; font-weight: 600;
  }
  table.repeater td { padding: 4px 7px; font-size: 9pt; border-bottom: 1px solid #eee; vertical-align: top; }
  table.repeater tr:nth-child(even) td { background: #faf8f4; }

  /* ---- Alerts / callouts ---- */
  .alert { padding: 7px 10px; border-radius: 3px; margin: 6px 0; font-size: 9pt; page-break-inside: avoid; }
  .alert .alert-body { margin: 0; }
  .alert ul { margin: 4px 0 0 0; padding-left: 16px; }
  .alert.INFO    { background: #eef4fb; border-left: 3px solid #3a76c4; }
  .alert.WARNING { background: #fdf6e7; border-left: 3px solid #c79a2b; }
  .alert.SUCCESS { background: #ecf6ee; border-left: 3px solid #3a9d52; }
  .alert.ERROR   { background: #fcebec; border-left: 3px solid #c4373a; }

  .info-block { margin: 8px 0; }
  .info-block .info-title { font-weight: 700; color: ${primary}; font-size: 10pt; margin-bottom: 3px; }
  .info-block .info-body { color: #444; font-size: 9.5pt; white-space: pre-wrap; }

  /* ---- Footer note ---- */
  .doc-footer { margin-top: 22px; padding-top: 6px; border-top: 1px solid #e3d9c9; color: #999; font-size: 8pt; }
`;
};
exports.buildFormPdfStyles = buildFormPdfStyles;
